import { NoDesignerText } from "constants/text";
import { OrderStatus } from "types/declarations/enums.ts";
import { FilterOpts } from "pages/OrderPages/OrderListPage/OrderListPage";
import { Dispatch, Fragment, SetStateAction, useEffect, useMemo } from "react";
import { displayDesigner, filterSpecificBrands, getOrderTypeDisplayLabel } from "utils/order";
import ChipDropdownMenu, { ChipDropdownMenuProps, ItemType } from "./ChipDropdownMenu";
import { FilterType } from "./filterType";
import OpenOrdersToggle from "./OpenOrdersToggle";
import { arraysEqual } from "utils/array";
import Divider from "@mui/material/Divider";
import { models } from "types/api/viewModels.ts";
import { getSingleOrderGlobal } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import { SearchBarFilterWrapper } from "components/OrderSearch/OrderSearch.styles.ts";

const SearchBarFilters = ({ orders, userDetails, filterOptions, setFilterOptions }: SearchBarFilterProps) => {
	const accounts = useMemo(
		() =>
			orders
				?.map((order: models["OrderViewModel"]) => {
					return [order.accountNumber, order.accountName].filter(Boolean).join(" - ");
				})
				.filter((item: string, index: number, arr: string[]) => arr.indexOf(item) === index)
				.sort(),
		[orders]
	);

	const orderTypes = useMemo(
		() =>
			orders
				?.reduce((accum: string[], order) => {
					if (order.orderType) {
						accum.push(getOrderTypeDisplayLabel(order.orderType));
					}
					return accum;
				}, [])
				.filter((item, index, arr) => arr.indexOf(item) === index)
				.sort(),
		[orders]
	);

	const orderStatuses = useMemo(() => {
		const orderStatusChronological: string[] = Object.values(OrderStatus);
		const statuses = orders
			?.map((order) => order.status)
			?.filter((item, index, arr) => arr.indexOf(item) === index)
			?.sort((a: models["OrderStatus"] | undefined, b: models["OrderStatus"] | undefined) => {
				const indexA = orderStatusChronological.indexOf(String(a));
				const indexB = orderStatusChronological.indexOf(String(b));
				return indexA - indexB;
			});
		return (
			statuses?.reduce((accum: string[], status: models["OrderStatus"] | undefined) => {
				if (status) {
					accum.push(status.replace(/_/g, " "));
				}

				return accum;
			}, []) ?? []
		);
	}, [orders]);

	const designerNumber = useMemo(
		() =>
			orders
				?.map((order: models["OrderViewModel"]) => {
					return displayDesigner(order.designer) ?? NoDesignerText;
				})
				.filter((item: string, index: number, arr: string[]) => arr.indexOf(item) === index)
				.sort(),
		[orders]
	);

	const orderGlobals = orders?.map((order) => getSingleOrderGlobal(order));
	const brandsFromOrders = useMemo(() => orderGlobals?.map((global) => global?.brand?.description), [orderGlobals]);

	const userBrands = useMemo(
		() =>
			brandsFromOrders
				?.map((brand) => filterSpecificBrands(brand?.replace(/_/g, " ").toUpperCase())?.toLowerCase())
				?.filter((item, index, arr) => arr.indexOf(item) === index)
				?.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
				?.sort(),
		[brandsFromOrders]
	);

	const onFilterChange = (id: string, values: string[]) => {
		setFilterOptions((prev: FilterOpts) => ({
			...prev,
			[id]: values
		}));
	};
	const defaultFilterOptions = useMemo(() => {
		if (!filterOptions?.sortedBy || filterOptions.sortedBy.length === 0) {
			return {
				...filterOptions,
				sortedBy: ["Last Updated"]
			};
		}
		return filterOptions;
	}, [filterOptions]);

	useEffect(() => {
		setFilterOptions(defaultFilterOptions);
	}, [defaultFilterOptions, setFilterOptions]);

	const openOrderStatuses = useMemo(
		() =>
			orderStatuses?.filter(
				(status: string) =>
					status !== OrderStatus.DELIVERED &&
					status !== OrderStatus.CANCELLED &&
					status !== OrderStatus.UNKNOWN
			) ?? [],
		[orderStatuses]
	);

	const hasClosedOrderStatuses = useMemo(
		() =>
			orderStatuses?.some(
				(status: string) =>
					status === OrderStatus.DELIVERED ||
					status === OrderStatus.CANCELLED ||
					status === OrderStatus.UNKNOWN
			),
		[orderStatuses]
	);

	const hasOpenAndClosedStatuses = hasClosedOrderStatuses && openOrderStatuses?.length > 0;
	const isOpenOrdersToggledOn = useMemo(
		() => Boolean(arraysEqual(openOrderStatuses, filterOptions?.status ?? []) && hasOpenAndClosedStatuses),
		[filterOptions?.status, hasOpenAndClosedStatuses, openOrderStatuses]
	);

	const handleOpenOrdersToggle = () => onFilterChange("status", isOpenOrdersToggledOn ? [] : openOrderStatuses);

	const openOrdersToggle = hasOpenAndClosedStatuses ? (
		<OpenOrdersToggle
			isOpenOrdersToggledOn={isOpenOrdersToggledOn}
			handleOpenOrdersToggle={handleOpenOrdersToggle}
		/>
	) : null;

	const hasErrors = useMemo(
		() =>
			orders?.some(
				(order) => order.status === OrderStatus.SUBMITTED_WITH_ERRORS || order.status === OrderStatus.HOLD
			) ?? false,
		[orders]
	);

	const convertOrderStatuses = (): ItemType[] => {
		return orderStatuses?.map((status) => {
			return {
				value: status,
				error: (status === "SUBMITTED WITH ERRORS" || status === "HOLD") && hasErrors
			};
		});
	};

	const filterDetails: FilterDetails[] = [
		{
			selectedItems: defaultFilterOptions?.sortedBy ?? [],
			onChange: (v) => onFilterChange("sortedBy", v),
			filterType: FilterType.SORTED_BY,
			items: ["Last Updated", "Order Date"],
			dataTestId: "sorted-by-filter-chip",
			isSearchable: false,
			hasWarning: false,
			children: undefined,
			isVisible: true,
			GTMIdentifier: "sorted-by-filter-chip"
		},
		{
			selectedItems: filterOptions?.accounts,
			onChange: (v) => onFilterChange("accounts", v),
			filterType: FilterType.ACCOUNT,
			items: accounts,
			dataTestId: "all-accounts-filter-chip",
			isSearchable: true,
			hasWarning: false,
			children: undefined,
			isVisible: userDetails?.role !== "CARE",
			GTMIdentifier: "account-filter-chip"
		},
		{
			selectedItems: filterOptions?.designers,
			onChange: (v) => onFilterChange("designers", v),
			filterType: FilterType.DESIGNER,
			items: designerNumber,
			dataTestId: "all-designers-filter-chip",
			isSearchable: true,
			hasWarning: false,
			children: undefined,
			isVisible: true,
			GTMIdentifier: "designer-filter-chip"
		},
		{
			selectedItems: filterOptions?.brands,
			onChange: (v) => onFilterChange("brands", v),
			filterType: FilterType.BRAND,
			items: userBrands,
			dataTestId: "brands-filter-chip",
			isSearchable: false,
			hasWarning: false,
			children: undefined,
			isVisible: true,
			GTMIdentifier: "brand-filter-chip"
		},
		{
			selectedItems: filterOptions?.status,
			onChange: (v) => onFilterChange("status", v),
			filterType: FilterType.ORDER_STATUS,
			items: convertOrderStatuses(),
			dataTestId: "order-status-filter-chip",
			isSearchable: false,
			hasWarning: hasErrors,
			children: openOrdersToggle,
			isVisible: true,
			GTMIdentifier: "order-status-filter-chip"
		},
		{
			selectedItems: filterOptions?.types,
			onChange: (v) => onFilterChange("types", v),
			filterType: FilterType.ORDER_TYPE,
			items: orderTypes,
			dataTestId: "order-types-filter-chip",
			isSearchable: false,
			hasWarning: false,
			children: undefined,
			isVisible: true,
			GTMIdentifier: "order-type-filter-chip"
		}
	];

	return (
		<SearchBarFilterWrapper>
			{filterDetails.map(
				(filter) =>
					filter.isVisible && (
						<Fragment key={filter.filterType}>
							<ChipDropdownMenu
								selectedItems={filter.selectedItems}
								onChange={filter.onChange}
								filterType={filter.filterType}
								items={filter.items}
								data-testid={filter.dataTestId}
								dataTestId={filter.dataTestId}
								hasWarning={filter.hasWarning}
								isSearchable={filter.isSearchable}
								GTMIdentifier={filter.GTMIdentifier}
							>
								{filter.children}
							</ChipDropdownMenu>
							{filter.filterType === FilterType.SORTED_BY && (
								<Divider
									orientation="vertical"
									flexItem
									sx={{ margin: "0 15px 0px 8px" }}
								/>
							)}
						</Fragment>
					)
			)}
		</SearchBarFilterWrapper>
	);
};

export interface FilterDetails extends ChipDropdownMenuProps {
	dataTestId: string;
	isVisible: boolean;
	GTMIdentifier: string;
}

export interface SearchBarFilterProps {
	orders: models["OrderViewModel"][] | null | undefined;
	userDetails: models["UserViewModel"] | undefined;
	accounts: models["CustomerAccountViewModel"][] | null | undefined;
	filterOptions: FilterOpts;
	setFilterOptions: Dispatch<SetStateAction<FilterOpts>>;
}

export default SearchBarFilters;
